import { useMemo, useEffect } from "react";
import { useFormState, useFormContext } from "react-hook-form";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Divider from "@shared/components/ui-kit/data-display/Divider/Divider.web";
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Checkbox from "@shared/components/ui-kit/form/Checkbox/Checkbox.web";
import Collapsible from "@shared/components/ui-kit/disclosure/Collapsible/Collapsible.web";

import SectionHeader from "../SectionHeader.web";
import PublicCheckoutShippingAddressForm from "../../../review/delivery-methods/PublicCheckoutShippingAddressForm/PublicCheckoutShippingAddressForm.web";
import type { CheckoutFormData } from "../../hooks/useCheckoutValidation";
import { VALIDATION_FIELDS } from "../../hooks/useCheckoutValidation";

/**
 * Shipping Address Section
 *
 * This section manages shipping address validation.
 * It validates based on whether shipping is same as billing, or requires separate shipping address.
 * Uses React Hook Form context to update validation state in a unidirectional manner.
 */
export function ShippingAddressSection({
  checkoutForms,
  onSameAsBillingAddressChange,
  allowedCountries,
  isShippingSameAsBillingAddress,
  billingAddress,
}: {
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  onSameAsBillingAddressChange: (isSameAsBillingAddress: boolean) => void;
  allowedCountries: string[] | null;
  isShippingSameAsBillingAddress: boolean;
  billingAddress: {
    info_home_address_country: string;
  } | null;
}) {
  const { setValue, watch, formState } = useFormContext<CheckoutFormData>();
  const shippingAddressValid = watch(VALIDATION_FIELDS.shippingAddress);
  const isTouched = formState.touchedFields.shippingAddress ?? false;
  const validationAttempted = watch(VALIDATION_FIELDS.validationAttempted);
  const errorMessage = formState.errors.shippingAddress?.message;
  const isSameAsBillingAddressDisabled = useMemo(() => {
    if (!billingAddress || !allowedCountries || allowedCountries.length === 0) {
      return false;
    }
    return !allowedCountries.includes(billingAddress.info_home_address_country);
  }, [billingAddress, allowedCountries]);

  const { isValid: isBillingAddressValid } = useFormState({
    control: checkoutForms.billingAddressForm?.control,
  });

  // If the billing address is valid, and shipping should be the same as billing,
  // then the form automatically is valid.
  const isFormValid = useMemo(() => {
    return isShippingSameAsBillingAddress
      ? isBillingAddressValid
      : checkoutForms.shippingAddressForm.formState.isValid;
  }, [
    isBillingAddressValid,
    isShippingSameAsBillingAddress,
    checkoutForms.shippingAddressForm.formState.isValid,
  ]);

  /**
   * Update form context when shipping address validation changes.
   * This maintains unidirectional data flow.
   */
  useEffect(() => {
    setValue(VALIDATION_FIELDS.shippingAddress, isFormValid);
  }, [isFormValid]);

  // Show error when field is touched OR validation attempted (hybrid approach)
  const shouldShowError =
    (isTouched || validationAttempted) &&
    !shippingAddressValid &&
    !isShippingSameAsBillingAddress &&
    Boolean(errorMessage);

  return (
    <>
      <Box mt="xl" mb="xl">
        <Divider />
      </Box>

      <Box>
        <ShippingAddressForm
          checkoutForms={checkoutForms}
          isShippingSameAsBillingAddress={isShippingSameAsBillingAddress}
          isSameAsBillingAddressDisabled={isSameAsBillingAddressDisabled}
          allowedCountries={allowedCountries}
          isFormValid={isFormValid}
          shouldShowError={shouldShowError}
          errorMessage={errorMessage}
          onSameAsBillingAddressChange={(isSameAsBillingAddress) => {
            onSameAsBillingAddressChange(isSameAsBillingAddress);
          }}
        />
      </Box>
    </>
  );
}

function ShippingAddressForm({
  checkoutForms,
  isShippingSameAsBillingAddress,
  isSameAsBillingAddressDisabled,
  allowedCountries,
  isFormValid,
  shouldShowError,
  errorMessage,
  onSameAsBillingAddressChange,
}: {
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  isShippingSameAsBillingAddress: boolean;
  isSameAsBillingAddressDisabled: boolean;
  allowedCountries: string[] | null;
  isFormValid: boolean;
  shouldShowError: boolean;
  errorMessage?: string;
  onSameAsBillingAddressChange?: (isSameAsBillingAddress: boolean) => void;
}) {
  const { t } = useTranslation("checkout");

  return (
    <Box>
      <Box>
        <SectionHeader
          sectionNumber={3}
          title={t("Shipping address")}
          isValid={isFormValid}
        />
      </Box>

      <Flex flexDirection="column" gap="md">
        {!isSameAsBillingAddressDisabled && (
          <Box>
            <Checkbox
              isChecked={isShippingSameAsBillingAddress}
              onChange={(e) => {
                onSameAsBillingAddressChange?.(e.target.checked);
              }}
            >
              <Text fontSize="sm">{t("Same as billing address")}</Text>
            </Checkbox>
          </Box>
        )}

        <Collapsible
          in={!isShippingSameAsBillingAddress}
          style={{
            margin: "-2px", // Allow outline of input elements to be visible
          }}
        >
          <Box
            margin="none" // Allow outline of input elements to be visible
          >
            <PublicCheckoutShippingAddressForm
              form={checkoutForms.shippingAddressForm}
              allowedCountries={allowedCountries}
            />
          </Box>
        </Collapsible>

        {/* Display inline error from RHF when validation attempted and invalid */}
        {shouldShowError && (
          <Box>
            <Text fontSize="sm" color="red.500" role="alert">
              {errorMessage}
            </Text>
          </Box>
        )}
      </Flex>
    </Box>
  );
}
